(() => {
  const menuKeys = {
    brands: "brands",
    "strategy + engineering": "strategy-engineering",
    manufacturing: "manufacturing",
    "live environments": "live-environments",
    "exo os": "exo-os",
    contact: "contact",
  };

  const menuKeyFromLink = (link) =>
    menuKeys[link?.textContent?.trim().toLocaleLowerCase()];

  function initBrandOrbit(menu) {
    const shell = menu?.querySelector("[data-header-brand-orbit-shell]");
    const stage = shell?.querySelector("[data-header-brand-orbit-rail]");
    const items = stage ? [...stage.querySelectorAll(":scope > .adaptive-header__brand-orbit-item")] : [];
    const status = shell?.querySelector("[data-header-brand-orbit-status]");
    if (!shell || !stage || items.length < 2 || shell.dataset.headerOrbitReady === "true") return;

    shell.dataset.headerOrbitReady = "true";
    const mobileQuery = window.matchMedia("(max-width: 720px)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const step = (Math.PI * 2) / items.length;
    let activeIndex = 0;
    let rotation = 0;
    let targetRotation = 0;
    let animationFrame = 0;
    let dragging = false;
    let moved = false;
    let pointerId = null;
    let pointerX = 0;
    let suppressClickUntil = 0;

    const itemName = (item) => item.querySelector("strong")?.textContent?.trim() || "Brand";

    const clearOrbit = () => {
      shell.classList.remove("is-orbit-ready", "is-dragging");
      items.forEach((item) => {
        item.style.removeProperty("--header-orbit-left");
        item.style.removeProperty("--header-orbit-top");
        item.style.removeProperty("--header-orbit-scale");
        item.style.removeProperty("--header-orbit-opacity");
        item.style.removeProperty("--header-orbit-blur");
        item.style.removeProperty("z-index");
        item.removeAttribute("aria-current");
      });
    };

    const render = () => {
      if (mobileQuery.matches) {
        clearOrbit();
        return;
      }

      shell.classList.add("is-orbit-ready");
      const geometry = items.map((item, index) => {
        const angle = rotation + index * step;
        const depth = (Math.cos(angle) + 1) / 2;
        return {
          item,
          index,
          left: 50 + Math.sin(angle) * 44,
          top: 48 + Math.cos(angle) * 31,
          depth,
        };
      });

      const nearest = geometry.reduce((front, candidate) =>
        candidate.depth > front.depth ? candidate : front,
      );
      activeIndex = nearest.index;

      geometry.forEach(({ item, index, left, top, depth }) => {
        const isActive = index === activeIndex;
        item.style.setProperty("--header-orbit-left", `${left.toFixed(2)}%`);
        item.style.setProperty("--header-orbit-top", `${top.toFixed(2)}%`);
        item.style.setProperty("--header-orbit-scale", (0.54 + depth * 0.46).toFixed(3));
        item.style.setProperty("--header-orbit-opacity", (0.34 + depth * 0.66).toFixed(3));
        item.style.setProperty("--header-orbit-blur", `${Math.max(0, (1 - depth) * 0.65).toFixed(2)}px`);
        item.style.zIndex = String(Math.round(10 + depth * 80));
        item.toggleAttribute("aria-current", isActive);
      });

      if (status) {
        status.value = `${String(activeIndex + 1).padStart(2, "0")} / ${String(items.length).padStart(2, "0")} — ${itemName(items[activeIndex])}`;
        status.textContent = status.value;
      }
    };

    const focusItem = (index) => {
      const destination = -index * step;
      const nearestTurn = Math.round((rotation - destination) / (Math.PI * 2));
      targetRotation = destination + nearestTurn * Math.PI * 2;
    };

    const move = (direction) => focusItem((activeIndex + direction + items.length) % items.length);

    shell.addEventListener("click", (event) => {
      const control = event.target.closest("[data-header-brand-orbit]");
      if (control) {
        event.preventDefault();
        move(control.dataset.headerBrandOrbit === "previous" ? -1 : 1);
        return;
      }
      if (Date.now() < suppressClickUntil && event.target.closest(".adaptive-header__brand-orbit-item")) {
        event.preventDefault();
      }
    });

    items.forEach((item, index) => {
      item.addEventListener("focus", () => {
        if (!mobileQuery.matches) focusItem(index);
      });
    });

    stage.addEventListener("keydown", (event) => {
      if (mobileQuery.matches) return;
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        move(event.key === "ArrowLeft" ? -1 : 1);
      } else if (event.key === "Home") {
        event.preventDefault();
        focusItem(0);
      } else if (event.key === "End") {
        event.preventDefault();
        focusItem(items.length - 1);
      }
    });

    stage.addEventListener("pointerdown", (event) => {
      if (mobileQuery.matches || event.button !== 0) return;
      dragging = true;
      moved = false;
      pointerId = event.pointerId;
      pointerX = event.clientX;
      targetRotation = rotation;
      shell.classList.add("is-dragging");
      stage.setPointerCapture?.(pointerId);
    });

    stage.addEventListener("pointermove", (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      const delta = event.clientX - pointerX;
      if (Math.abs(delta) > 2) moved = true;
      rotation += (delta / Math.max(stage.clientWidth, 1)) * Math.PI * 2;
      targetRotation = rotation;
      pointerX = event.clientX;
      render();
    });

    const finishDrag = (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      dragging = false;
      shell.classList.remove("is-dragging");
      stage.releasePointerCapture?.(pointerId);
      if (moved) {
        suppressClickUntil = Date.now() + 220;
        focusItem(activeIndex);
      }
      pointerId = null;
    };
    stage.addEventListener("pointerup", finishDrag);
    stage.addEventListener("pointercancel", finishDrag);

    const animate = () => {
      if (!shell.isConnected) return;
      if (!dragging && !mobileQuery.matches) {
        const difference = targetRotation - rotation;
        if (Math.abs(difference) > 0.0005) {
          rotation += difference * (reducedMotion.matches ? 1 : 0.14);
        } else {
          rotation = targetRotation;
        }
      }
      render();
      animationFrame = window.requestAnimationFrame(animate);
    };

    mobileQuery.addEventListener?.("change", render);
    render();
    animationFrame = window.requestAnimationFrame(animate);
  }

  function initHeaderMenus(root = document) {
    const header = root.querySelector("[data-exo-header]");
    const nav = header?.querySelector("[data-header-nav]");
    if (!header || !nav || header.dataset.headerMenusReady === "true") return;
    header.dataset.headerMenusReady = "true";

    const closeMenus = (except) => {
      header.querySelectorAll("[data-header-menu][open]").forEach((menu) => {
        if (menu !== except) menu.removeAttribute("open");
      });
    };

    const installMenus = () => {
      const isExoNavigation =
        (nav.getAttribute("aria-label") || "").toLocaleLowerCase() === "exo navigation";
      const installedMenus = [...nav.querySelectorAll(":scope > [data-header-menu]")];

      if (!isExoNavigation) {
        installedMenus.forEach((menu) => menu.remove());
        header.classList.remove("has-brand-menu", "has-header-menus");
        return;
      }

      [...nav.querySelectorAll(":scope > a")].forEach((link) => {
        const menuKey = menuKeyFromLink(link);
        const template = menuKey && header.querySelector(
          `[data-header-menu-template][data-header-menu-key="${menuKey}"]`,
        );
        if (!template) return;
        link.replaceWith(template.content.cloneNode(true));
      });

      const hasMenus = Boolean(nav.querySelector(":scope > [data-header-menu]"));
      const brandMenu = nav.querySelector(":scope > [data-header-brand-menu]");
      header.classList.toggle("has-brand-menu", Boolean(brandMenu));
      header.classList.toggle("has-header-menus", hasMenus);
      if (brandMenu) {
        document.dispatchEvent(
          new CustomEvent("exo:brand-orbit:load", { detail: { root: brandMenu } }),
        );
      }
    };

    const observer = new MutationObserver(installMenus);
    observer.observe(nav, { childList: true });
    document.addEventListener("pointerdown", (event) => {
      const activeMenu = header.querySelector("[data-header-menu][open]");
      if (activeMenu && !activeMenu.contains(event.target)) closeMenus();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeMenus();
    });
    nav.addEventListener("click", (event) => {
      const summary = event.target.closest("[data-header-menu] > summary");
      if (!summary) return;
      const menu = summary.parentElement;
      window.setTimeout(() => {
        if (menu.open) {
          closeMenus(menu);
          const brandsPanel = menu.querySelector(".adaptive-header__brands-panel");
          if (brandsPanel && window.innerWidth > 720) {
            const panelTop = Math.round(summary.getBoundingClientRect().bottom + 18);
            brandsPanel.style.setProperty("--header-brands-panel-top", `${panelTop}px`);
          }
        }
      }, 0);
    });
    installMenus();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initHeaderMenus());
  } else {
    initHeaderMenus();
  }
  document.addEventListener("shopify:section:load", (event) => initHeaderMenus(event.target));
})();
